import React from 'react';
import { Check, X, Crown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

interface ComparisonFeature {
  feature: string;
  munger: boolean | string;
  apollo: boolean | string;
  smartlead: boolean | string;
  instantly: boolean | string;
  diy: boolean | string;
}

const comparisonData: ComparisonFeature[] = [
  {
    feature: 'Time to first meeting booked',
    munger: '24-48 hours',
    apollo: '2-4 weeks',
    smartlead: '1-3 weeks',
    instantly: '1-2 weeks',
    diy: '3-6 weeks'
  },
  {
    feature: 'Setup complexity',
    munger: '2 minutes',
    apollo: '20+ hours',
    smartlead: '10+ hours',
    instantly: '15+ hours',
    diy: '40+ hours'
  },
  {
    feature: 'Guaranteed open rate',
    munger: '25-45%',
    apollo: '8-15%',
    smartlead: '10-18%',
    instantly: '12-20%',
    diy: '5-10%'
  },
  {
    feature: 'Pre-warmed infrastructure',
    munger: '70+ domains ready',
    apollo: 'Use your domain',
    smartlead: 'DIY warmup',
    instantly: 'DIY warmup',
    diy: 'Start from zero'
  },
  {
    feature: 'AI prospect research',
    munger: '250M+ database',
    apollo: 'Buy separately',
    smartlead: 'Not included',
    instantly: 'Not included',
    diy: 'Manual research'
  },
  {
    feature: 'Email personalization',
    munger: 'AI reads LinkedIn',
    apollo: 'Mail merge only',
    smartlead: 'Basic variables',
    instantly: 'Basic variables',
    diy: 'Manual writing'
  },
  {
    feature: 'Reply handling',
    munger: 'AI books meetings',
    apollo: 'You respond',
    smartlead: 'You respond',
    instantly: 'You respond',
    diy: 'You respond'
  },
  {
    feature: 'Hidden costs',
    munger: 'None - all inclusive',
    apollo: '+$500 warmup',
    smartlead: '+$200 extras',
    instantly: '+$300 tools',
    diy: '+$1000s in tools'
  },
  {
    feature: 'Real monthly cost',
    munger: 'Flat fee, all-inclusive',
    apollo: '$649+',
    smartlead: '$323+',
    instantly: '$464+',
    diy: '$2000+'
  },
  {
    feature: 'Time investment/month',
    munger: '0 hours',
    apollo: '40+ hours',
    smartlead: '60+ hours',
    instantly: '50+ hours',
    diy: '100+ hours'
  },
  {
    feature: 'Meetings booked/month',
    munger: '20-60',
    apollo: '5-15',
    smartlead: '5-20',
    instantly: '10-25',
    diy: '0-10'
  }
];

const competitors = [
  { key: 'munger', name: 'Munger.ai', highlight: true },
  { key: 'apollo', name: 'Apollo' },
  { key: 'smartlead', name: 'SmartLead' },
  { key: 'instantly', name: 'Instantly' },
  { key: 'diy', name: 'DIY Setup' }
];

interface CompetitorComparisonProps {
  onStartClick?: () => void;
}

const CompetitorComparison: React.FC<CompetitorComparisonProps> = ({ onStartClick }) => {
  const renderCell = (value: boolean | string, isHighlight: boolean = false) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className={`w-5 h-5 mx-auto ${isHighlight ? 'text-green-600' : 'text-green-500'}`} />
      ) : (
        <X className={`w-5 h-5 mx-auto ${isHighlight ? 'text-red-500' : 'text-gray-400'}`} />
      );
    }
    
    return (
      <span className={`text-sm ${isHighlight ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
        {value}
      </span>
    );
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-purple-100 text-purple-800">
            Competitive Analysis
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why Choose Munger.ai?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how we compare to other solutions. <span className="font-semibold">We're the only automated option.</span>
          </p>
        </div>

        {/* Mobile-first responsive table */}
        <div className="relative bg-white rounded-2xl shadow-xl overflow-visible border border-gray-200 mt-8">
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto rounded-2xl">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-6 font-semibold text-gray-900 bg-gray-50">
                    Features
                  </th>
                  {competitors.map((competitor) => (
                    <th 
                      key={competitor.key}
                      className={`text-center p-6 font-semibold relative ${
                        competitor.highlight 
                          ? 'bg-blue-50 text-blue-900 border-l-2 border-r-2 border-blue-200' 
                          : 'bg-gray-50 text-gray-900'
                      }`}
                    >
                      {competitor.highlight && (
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-10">
                          <Badge className="bg-blue-600 text-white px-3 py-1 shadow-lg">
                            <Crown className="w-4 h-4 mr-1" />
                            Best Choice
                          </Badge>
                        </div>
                      )}
                      {competitor.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="p-6 font-medium text-gray-900 bg-gray-50">
                      {row.feature}
                    </td>
                    <td className="p-6 text-center bg-blue-50 border-l-2 border-r-2 border-blue-200">
                      {renderCell(row.munger, true)}
                    </td>
                    <td className="p-6 text-center">
                      {renderCell(row.apollo)}
                    </td>
                    <td className="p-6 text-center">
                      {renderCell(row.smartlead)}
                    </td>
                    <td className="p-6 text-center">
                      {renderCell(row.instantly)}
                    </td>
                    <td className="p-6 text-center">
                      {renderCell(row.diy)}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden p-6 space-y-6">
            {comparisonData.map((row, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-gray-200 rounded-lg p-4"
              >
                <h3 className="font-semibold text-gray-900 mb-4">{row.feature}</h3>
                <div className="grid grid-cols-2 gap-4">
                  {competitors.map((competitor) => (
                    <div 
                      key={competitor.key}
                      className={`text-center p-3 rounded-lg ${
                        competitor.highlight 
                          ? 'bg-blue-50 border border-blue-200' 
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {competitor.name}
                      </div>
                      <div>
                        {renderCell(row[competitor.key as keyof ComparisonFeature], competitor.highlight)}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">10x</div>
            <div className="text-gray-600">Faster Setup</div>
            <div className="text-sm text-gray-500">2 minutes vs 1-2 weeks with DIY</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">50%</div>
            <div className="text-gray-600">Lower Total Cost</div>
            <div className="text-sm text-gray-500">Including all hidden fees & setup time</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-600 mb-2">90%</div>
            <div className="text-gray-600">Automated</div>
            <div className="text-sm text-gray-500">Most hands-off solution available</div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={onStartClick}
          >
            Book a Call
          </Button>
          <p className="text-sm text-gray-600 mt-2">
            Join 2,100+ companies who chose the automated solution
          </p>
        </div>
      </div>
    </section>
  );
};

export default CompetitorComparison; 